import * as Phaser from 'phaser';
import { PALETTE } from '../render/palette';

export class Exit extends Phaser.GameObjects.Container {
  public unlocked = false;
  private readonly lockedGroup: Phaser.GameObjects.Container;
  private readonly portalGroup: Phaser.GameObjects.Container;
  // Hit target — covers the cell so pointer events still register both
  // before and after the entity flips its visual mode.
  private readonly hitTarget: Phaser.GameObjects.Rectangle;
  private portalTween?: Phaser.Tweens.Tween;

  constructor(scene: Phaser.Scene, cellSize: number, gridX: number, gridY: number) {
    super(scene, (gridX + 0.5) * cellSize, (gridY + 0.5) * cellSize);

    this.hitTarget = scene.add
      .rectangle(0, 0, cellSize * 0.85, cellSize * 0.85, 0xffffff, 0)
      .setOrigin(0.5);
    this.hitTarget.setInteractive({ useHandCursor: true });
    this.hitTarget.on('pointerdown', () => this.emit('pointerdown'));

    this.lockedGroup = this.buildLocked(scene, cellSize);
    this.portalGroup = this.buildPortal(scene, cellSize);
    this.portalGroup.setVisible(false);

    this.add([this.hitTarget, this.lockedGroup, this.portalGroup]);
    scene.add.existing(this);
  }

  setUnlocked(unlocked: boolean): void {
    if (this.unlocked === unlocked) return;
    this.unlocked = unlocked;
    this.lockedGroup.setVisible(!unlocked);
    this.portalGroup.setVisible(unlocked);

    if (unlocked && !this.portalTween) {
      // Find the outer halo (first child of portalGroup) and start its pulse.
      const outerHalo = this.portalGroup.getAt(0);
      this.portalTween = this.scene.tweens.add({
        targets: outerHalo,
        alpha: { from: 0.4, to: 0.9 },
        scaleX: { from: 1, to: 1.08 },
        scaleY: { from: 1, to: 1.08 },
        duration: 1400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    }
  }

  private buildLocked(scene: Phaser.Scene, cellSize: number): Phaser.GameObjects.Container {
    const g = scene.add.graphics();
    const half = cellSize * 0.28; // bulkhead extends from -half to +half
    const radius = 4;

    // Bulkhead body.
    g.fillStyle(PALETTE.EXIT_BULKHEAD, 1);
    g.fillRoundedRect(-half, -half, half * 2, half * 2, radius);
    g.lineStyle(1, PALETTE.EXIT_BULKHEAD_STROKE, 1);
    g.strokeRoundedRect(-half, -half, half * 2, half * 2, radius);

    // Centre seam — implies the door splits in half.
    g.lineStyle(1.5, PALETTE.EXIT_BULKHEAD_SEAM, 1);
    g.lineBetween(-half, 0, half, 0);

    // Top + bottom faint panel lines.
    g.lineStyle(0.5, PALETTE.EXIT_BULKHEAD_STROKE, 1);
    g.lineBetween(-half + 4, -half + 4, half - 4, -half + 4);
    g.lineBetween(-half + 4, half - 4, half - 4, half - 4);

    // Centre padlock.
    const lock = scene.add.graphics();
    const lockScale = cellSize / 64; // padlock metrics designed at 64px cells
    lock.setScale(lockScale * 0.85);
    // Shackle arc.
    lock.lineStyle(1.5, PALETTE.W, 0.9);
    lock.beginPath();
    lock.arc(0, -4, 5, Math.PI, 0, false);
    lock.lineTo(5, 0);
    lock.moveTo(-5, -4);
    lock.lineTo(-5, 0);
    lock.strokePath();
    // Body.
    lock.fillStyle(PALETTE.W, 0.9);
    lock.fillRoundedRect(-6, 0, 12, 9, 1.5);
    // Keyhole.
    lock.fillStyle(PALETTE.EXIT_BULKHEAD, 1);
    lock.fillCircle(0, 4.5, 1.2);

    const container = scene.add.container(0, 0, [g, lock]);
    return container;
  }

  private buildPortal(scene: Phaser.Scene, cellSize: number): Phaser.GameObjects.Container {
    const outerHalo = scene.add
      .circle(0, 0, cellSize * 0.4, PALETTE.W, 0.4)
      .setOrigin(0.5)
      .setBlendMode(Phaser.BlendModes.ADD);
    const midHalo = scene.add
      .circle(0, 0, cellSize * 0.28, PALETTE.W, 0.18)
      .setOrigin(0.5)
      .setBlendMode(Phaser.BlendModes.ADD);
    const core = scene.add.circle(0, 0, cellSize * 0.17, PALETTE.W, 0.85).setOrigin(0.5);
    const hotCore = scene.add.circle(0, 0, cellSize * 0.08, 0xffffff, 1).setOrigin(0.5);

    // 8 radial rays.
    const rays = scene.add.graphics();
    rays.lineStyle(0.8, PALETTE.W, 0.5);
    for (let i = 0; i < 8; i++) {
      const a = (i * Math.PI) / 4;
      const x1 = Math.cos(a) * cellSize * 0.22;
      const y1 = Math.sin(a) * cellSize * 0.22;
      const x2 = Math.cos(a) * cellSize * 0.35;
      const y2 = Math.sin(a) * cellSize * 0.35;
      rays.lineBetween(x1, y1, x2, y2);
    }

    // Small open-padlock annotation, drifting up-right.
    const openLock = scene.add.graphics();
    openLock.setPosition(cellSize * 0.22, -cellSize * 0.28);
    openLock.setScale(0.65);
    openLock.lineStyle(1.4, PALETTE.G, 1);
    openLock.beginPath();
    // Open shackle: arc that doesn't close all the way.
    openLock.arc(0, -4, 4, Math.PI, Math.PI * 1.5, false);
    openLock.strokePath();
    openLock.lineStyle(1.4, PALETTE.G, 1);
    openLock.beginPath();
    openLock.moveTo(4, -4);
    openLock.lineTo(4, 0);
    openLock.strokePath();
    openLock.fillStyle(PALETTE.G, 0.85);
    openLock.fillRoundedRect(-5, 0, 10, 8, 1.5);

    return scene.add.container(0, 0, [outerHalo, midHalo, core, hotCore, rays, openLock]);
  }
}
